import axios, { AxiosInstance, AxiosError } from 'axios';
import { USER_SVC_BASE_URL } from 'utils/constants/common';
import { UserDetail } from 'structure/providers/AuthProvider';
import {
  getStoredTokens,
  removeToken,
} from 'structure/portfolio/utils/portfolio';

interface FetchNonce {
  nonce: string;
  validUntil: number;
}

interface OwnerInfo {
  success: boolean;
  jwt?: string;
}

interface RefreshData {
  updatedAt: number;
  refreshData: boolean;
}

const isConnectivityIssue = (error: AxiosError) => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return true;
  }

  if (error.code === 'ERR_NETWORK') {
    return true;
  }

  if (!error.response && error.request) {
    return true;
  }

  return false;
};

const appendConnectivityMetadata = (error: AxiosError) => {
  const reason =
    typeof navigator !== 'undefined' && !navigator.onLine
      ? 'offline'
      : 'unreachable';

  return Object.assign(error, {
    isConnectivityError: true,
    connectivityReason: reason,
  });
};

export class PortfolioAPI {
  private axiosInstance: AxiosInstance;
  private requestInterceptor: number | null;
  private responseInterceptor: number | null;

  constructor({ baseURL }: { baseURL: string }) {
    this.axiosInstance = axios.create({
      baseURL: baseURL,
    });
    this.requestInterceptor = null;
    this.responseInterceptor = null;
    this.setupResponseInterceptor();
  }

  private setupResponseInterceptor(): void {
    this.responseInterceptor = this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (axios.isAxiosError(error) && isConnectivityIssue(error)) {
          return Promise.reject(appendConnectivityMetadata(error));
        }

        if (error.response?.status === 401) {
          try {
            // Extract address from the request config and remove only that user's token
            const requestAddress = this.getAddressFromRequest(error.config);
            if (requestAddress) {
              removeToken(requestAddress);
            }
          } catch (disconnectError) {
            console.error(disconnectError);
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getAddressFromRequest(config: any): string | null {
    try {
      // Try to extract address from the Authorization header
      const authHeader = config.headers?.Authorization;
      if (authHeader && authHeader.startsWith('Bearer ')) {
        // We need to find which address this token belongs to
        const tokens = getStoredTokens();
        for (const [address, token] of Object.entries(tokens)) {
          if (authHeader === `Bearer ${token}`) {
            return address;
          }
        }
      }

      // Fallback: try to extract from URL params or body
      if (config.params?.address) {
        return config.params.address;
      }

      if (config.data?.address) {
        return config.data.address;
      }

      return null;
    } catch {
      return null;
    }
  }

  async setToken(address: string) {
    const token = getStoredTokens();
    if (address && token[address]) {
      if (typeof this.requestInterceptor === 'number') {
        this.axiosInstance.interceptors.request.eject(this.requestInterceptor);
      }
      this.requestInterceptor = this.axiosInstance.interceptors.request.use(
        (config) => {
          if (token[address]) {
            config.headers.Authorization = `Bearer ${token[address]}`;
          }
          return config;
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    }
  }

  async getUserDetails({
    address,
  }: {
    address: string;
  }): Promise<UserDetail | null> {
    try {
      this.setToken(address);
      const response = await this.axiosInstance.get('/users');
      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async fetchNonce({
    address,
  }: {
    address: string;
  }): Promise<FetchNonce | null> {
    try {
      this.setToken(address);
      const response = await this.axiosInstance.post('/auth/sign', { address });
      return response.data;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async verifyOwnerInfo({
    address,
    signature,
    chainId,
    message,
  }: {
    address: string;
    signature: string;
    chainId: number;
    message: string;
  }): Promise<OwnerInfo> {
    try {
      this.setToken(address);
      const response = await this.axiosInstance.post('/auth/verify', {
        address,
        signature,
        chainId,
        message,
      });
      return response.data;
    } catch (error) {
      console.error(error);
      return {
        success: false,
      } as OwnerInfo;
    }
  }

  async refreshUser({ address }: { address: string }): Promise<RefreshData> {
    try {
      this.setToken(address);
      const url = `users/refresh`;
      const result = await this.axiosInstance.post<boolean>(url);
      const currentTimestamp = Math.floor(Date.now() / 1000);
      if (result.status === 201) {
        return {
          updatedAt: currentTimestamp,
          refreshData: result.data,
        };
      } else {
        return {
          updatedAt: currentTimestamp,
          refreshData: false,
        };
      }
    } catch (error) {
      console.error(error);
      const currentTimestamp = Math.floor(Date.now() / 1000);
      return {
        updatedAt: currentTimestamp,
        refreshData: false,
      };
    }
  }
}

export const portfolioAPI = new PortfolioAPI({
  baseURL: USER_SVC_BASE_URL,
});
